<script setup lang="ts">
import {
  readActiveOrganisationUser,
  type ContractUserShareInDb,
  type ContractualPartyUserShareInDb,
  type PermissionRoleInDb,
  type UserInDb,
} from '@/api';
import {
  NButton,
  NDropdown,
  NIcon,
  useDialog,
  type DataTableColumns,
  type DropdownOption,
} from 'naive-ui';
import { computed, h, onMounted, ref, watch, type Component, type VNode } from 'vue';
import { RouterLink, useRoute, useRouter, type RouteLocationRaw } from 'vue-router';
import {
  IconDelete,
  IconDocumentText,
  IconEdit,
  IconEye,
  IconMore,
  IconPeople,
  IconShieldCheck,
  IconUserRemove,
} from '@/components/design/icons';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import VaultSharesTable from '@/components/file_vault/VaultSharesTable.vue';
import GaiaTabBar, { type GaiaTabBarItem } from '@/components/design/composite/GaiaTabBar.vue';
import AssignRoleToUserModal from '@/components/permissions/AssignRoleToUserModal.vue';
import OrganisationSeveranceModal from '@/components/organisation/OrganisationSeveranceModal.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useMemberActions } from '@/composables/useMemberActions';
import { ContractShareService } from '@/services/contractShareService';
import { ContractualPartyShareService } from '@/services/contractualPartyShareService';
import { PermissionsService, isAdminRole } from '@/services/permissionsService';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';
import { ACCESS_LEVEL_OPTIONS, formatAccessPermissions } from '@/utils/accessPermission';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import { extractErrorDetail, isAccessDenied } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import {
  contractRoute as contractDetailRoute,
  contractualPartyRoute,
  permissionRoleRoute,
} from '@/router/links';

/**
 * One member of the organisation, as an org admin sees them: who they are,
 * which roles they hold, and which contracts are shared with them directly.
 * The same shape as the role page — header, tabs, one list at a time. Your own
 * profile (/users/me) is UserDetailView.
 */

type MemberTab = 'roles' | 'contracts' | 'parties' | 'files';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const message = useGaiaToast();
const dialog = useDialog();

const userUid = computed(() => route.params.uid as string);
const isAdmin = computed(() => authStore.userPermissions.is_org_admin);

const { activeTab } = useListQuerySync({
  defaultTab: 'roles',
  allowedTabs: ['roles', 'contracts', 'parties', 'files'],
});

const user = ref<UserInDb | null>(null);
const loading = ref(false);

async function fetchUser(): Promise<void> {
  loading.value = true;
  try {
    const response = await readActiveOrganisationUser({ path: { uid: userUid.value } });
    if (response.error) throw new Error(extractErrorDetail(response.error, 'Failed to load user'));
    user.value = response.data;
  } catch (error) {
    // A refused member renders the page's "Member not found" state; no toast on top.
    if (isAccessDenied(error)) {
      logger.warn('Member not available:', error);
    } else {
      message.error(error instanceof Error ? error.message : 'Failed to load user');
    }
    user.value = null;
  } finally {
    loading.value = false;
  }
}

// ==================== Header ====================

function menuIcon(icon: Component): () => VNode {
  return () => h(NIcon, null, { default: () => h(icon) });
}

const memberActions = useMemberActions({
  onChanged: fetchUser,
  // They are no longer in the organisation, so their page is gone too.
  onRemoved: async () => {
    await router.push({ name: 'settings-users' });
  },
});
const { severance, severanceLoading, severanceError, closeSeverance, confirmSeverance } =
  memberActions;

/** An empty name falls back to the email, so this is a truthiness check on purpose. */
function nameOrEmail(member: UserInDb | null): string {
  if (!member) return '';
  return member.full_name.trim() || member.email;
}

const displayName = computed(() => nameOrEmail(user.value) || 'Member');

const initials = computed(() => {
  const source = nameOrEmail(user.value);
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : source.slice(0, 2)).toUpperCase();
});

const isSelf = computed(() => authStore.backendUser?.uid === user.value?.uid);

// ==================== Roles ====================

const roles = ref<PermissionRoleInDb[]>([]);
const loadingRoles = ref(false);
const showAssignRole = ref(false);
const assigningRoleUid = ref<string | null>(null);

const holdsAdminRole = computed(() => roles.value.some(role => isAdminRole(role)));

async function fetchRoles(): Promise<void> {
  loadingRoles.value = true;
  try {
    roles.value = await PermissionsService.listRolesForUser(userUid.value);
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to load roles');
    roles.value = [];
  } finally {
    loadingRoles.value = false;
  }
}

async function handleAssignRole(role: PermissionRoleInDb): Promise<void> {
  if (!isAdmin.value) return;
  assigningRoleUid.value = role.uid;
  try {
    await PermissionsService.assignUsersToRole(role.uid, [userUid.value]);
    message.success(`${displayName.value} now has the ${role.name} role`);
    await fetchRoles();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to assign role');
  } finally {
    assigningRoleUid.value = null;
  }
}

function confirmRemoveRole(role: PermissionRoleInDb): void {
  dialog.warning({
    title: `Remove the ${role.name} role?`,
    content: `${displayName.value} stays in the organisation but loses what this role allows.`,
    positiveText: 'Remove role',
    negativeText: 'Cancel',
    positiveButtonProps: { type: 'error' },
    onPositiveClick: async () => {
      try {
        await PermissionsService.removeUsersFromRole(role.uid, [userUid.value]);
        message.success(`Removed the ${role.name} role`);
        await fetchRoles();
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Failed to remove role');
      }
    },
  });
}

function roleRoute(role: PermissionRoleInDb): RouteLocationRaw {
  return permissionRoleRoute(role.uid);
}

function openRole(role: PermissionRoleInDb): void {
  void router.push(roleRoute(role));
}

// ==================== Shared contracts ====================

const sharedContracts = ref<ContractUserShareInDb[]>([]);
const loadingContracts = ref(false);

async function fetchSharedContracts(): Promise<void> {
  loadingContracts.value = true;
  try {
    sharedContracts.value = await ContractShareService.getContractSharesByUser(userUid.value);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Failed to load contracts shared with this member'
    );
    sharedContracts.value = [];
  } finally {
    loadingContracts.value = false;
  }
}

const sharedParties = ref<ContractualPartyUserShareInDb[]>([]);
const loadingParties = ref(false);

async function fetchSharedParties(): Promise<void> {
  loadingParties.value = true;
  try {
    sharedParties.value = await ContractualPartyShareService.getPartySharesByUser(userUid.value);
  } catch (error) {
    message.error(
      error instanceof Error ? error.message : 'Failed to load parties shared with this member'
    );
    sharedParties.value = [];
  } finally {
    loadingParties.value = false;
  }
}

/** One share, contract or party — the two behave identically here. */
interface ShareTarget {
  kind: 'contract' | 'party';
  uid: string;
  name: string;
  permissions: number;
}

const SHARE_SERVICE = {
  contract: {
    update: (uid: string, bitmask: number): Promise<unknown> =>
      ContractShareService.updateUserSharePermissions(uid, userUid.value, bitmask),
    remove: (uid: string): Promise<unknown> =>
      ContractShareService.removeUserShare(uid, userUid.value),
    refresh: fetchSharedContracts,
  },
  party: {
    update: (uid: string, bitmask: number): Promise<unknown> =>
      ContractualPartyShareService.updateUserSharePermissions(uid, userUid.value, bitmask),
    remove: (uid: string): Promise<unknown> =>
      ContractualPartyShareService.removeUserShare(uid, userUid.value),
    refresh: fetchSharedParties,
  },
};

const editingShare = ref<ShareTarget | null>(null);
const editShareBitmask = ref(0);
const savingShare = ref(false);

function openEditShare(target: ShareTarget): void {
  editingShare.value = target;
  editShareBitmask.value = target.permissions;
}

async function saveShare(): Promise<void> {
  const target = editingShare.value;
  if (!target || editShareBitmask.value === 0 || savingShare.value) return;
  savingShare.value = true;
  try {
    await SHARE_SERVICE[target.kind].update(target.uid, editShareBitmask.value);
    message.success('Access updated');
    editingShare.value = null;
    await SHARE_SERVICE[target.kind].refresh();
  } catch (error) {
    message.error(error instanceof Error ? error.message : 'Failed to update access');
  } finally {
    savingShare.value = false;
  }
}

function confirmRemoveShare(target: ShareTarget): void {
  const name = target.name;
  dialog.warning({
    title: `Stop sharing ${name}?`,
    content: `${displayName.value} loses the access this share gave them. Access through their roles stays.`,
    positiveText: 'Stop sharing',
    negativeText: 'Cancel',
    positiveButtonProps: { type: 'error' },
    onPositiveClick: async () => {
      try {
        await SHARE_SERVICE[target.kind].remove(target.uid);
        message.success(`${name} is no longer shared with ${displayName.value}`);
        await SHARE_SERVICE[target.kind].refresh();
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Failed to stop sharing');
      }
    },
  });
}

// ==================== Tabs ====================

/** Reported by the Files & folders table once it has loaded. */
const sharedVaultItemCount = ref<number | null>(null);

/** Contracts get a tab when the organisation has the feature, or something is shared anyway. */

const tabs = computed((): GaiaTabBarItem[] => {
  const items: GaiaTabBarItem[] = [{ key: 'roles', label: 'Roles', count: roles.value.length }];
  items.push(
    { key: 'contracts', label: 'Contracts', count: sharedContracts.value.length },
    { key: 'parties', label: 'Parties', count: sharedParties.value.length },
    { key: 'files', label: 'Files & folders', count: sharedVaultItemCount.value ?? undefined }
  );
  return items;
});

const currentTab = computed(
  (): MemberTab =>
    (tabs.value.some(tab => tab.key === activeTab.value) ? activeTab.value : 'roles') as MemberTab
);

// ==================== Table cells ====================

/** A row's "⋯" menu. Clicks stop here so the row doesn't also open. */
function renderRowMenu(
  options: DropdownOption[],
  onSelect: (key: string) => void,
  label: string
): VNode {
  return h(
    'span',
    {
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
      },
    },
    [
      h(
        NDropdown,
        {
          trigger: 'click',
          placement: 'bottom-end',
          options,
          onSelect: (key: string | number) => {
            onSelect(String(key));
          },
        },
        {
          default: () =>
            h(
              NButton,
              { quaternary: true, size: 'small', 'aria-label': label, title: label },
              { icon: () => h(NIcon, null, { default: () => h(IconMore) }) }
            ),
        }
      ),
    ]
  );
}

/** The primary line links to what the row lists, so cmd- and middle-click open it in a new tab. */
function renderTitle(
  to: RouteLocationRaw,
  primary: string,
  secondary?: string | null,
  tag?: string
): VNode {
  return h('div', { style: { display: 'flex', flexDirection: 'column', minWidth: 0 } }, [
    h('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } }, [
      h(
        RouterLink,
        {
          to,
          style: 'color: inherit; text-decoration: none',
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
          },
        },
        {
          default: () =>
            h(GaiaText, { size: 'body', weight: 'semibold', tone: 'ink' }, () => primary),
        }
      ),
      tag ? h(GaiaTag, { variant: 'muted', size: 'small' }, () => tag) : null,
    ]),
    secondary ? h(GaiaText, { size: 'control', tone: 'subtle' }, () => secondary) : null,
  ]);
}

const roleColumns = computed((): DataTableColumns<PermissionRoleInDb> => [
  {
    title: 'Role',
    key: 'name',
    render: row =>
      renderTitle(
        roleRoute(row),
        row.name,
        row.description,
        row.protected ? 'Built-in' : undefined
      ),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      renderRowMenu(
        [
          { key: 'open', label: 'Open role', icon: menuIcon(IconEye) },
          ...(isAdmin.value
            ? [
                { type: 'divider', key: 'divider' },
                { key: 'remove', label: 'Remove role', icon: menuIcon(IconUserRemove) },
              ]
            : []),
        ],
        key => {
          if (key === 'open') openRole(row);
          if (key === 'remove') confirmRemoveRole(row);
        },
        `Actions for ${row.name}`
      ),
  },
]);

const contractColumns = computed((): DataTableColumns<ContractUserShareInDb> => [
  {
    title: 'Contract',
    key: 'contract',
    render: row =>
      renderTitle(
        contractRoute(row),
        row.contract.name || 'Untitled contract',
        row.contract.contract_type?.name
      ),
  },
  {
    title: 'Access',
    key: 'permissions',
    width: 200,
    render: row =>
      h(GaiaTag, { variant: 'muted', size: 'small' }, () =>
        formatAccessPermissions(row.permissions)
      ),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      isAdmin.value
        ? renderRowMenu(
            [
              { key: 'access', label: 'Change access', icon: menuIcon(IconEdit) },
              { type: 'divider', key: 'divider' },
              { key: 'remove', label: 'Stop sharing', icon: menuIcon(IconDelete) },
            ],
            key => {
              onShareMenu(
                {
                  kind: 'contract',
                  uid: row.contract.uid,
                  name: row.contract.name || 'this contract',
                  permissions: row.permissions,
                },
                key
              );
            },
            `Actions for ${row.contract.name}`
          )
        : h('span'),
  },
]);

const SHARE_MENU: DropdownOption[] = [
  { key: 'access', label: 'Change access', icon: menuIcon(IconEdit) },
  { type: 'divider', key: 'divider' },
  { key: 'remove', label: 'Stop sharing', icon: menuIcon(IconDelete) },
];

function onShareMenu(target: ShareTarget, key: string): void {
  if (key === 'access') openEditShare(target);
  if (key === 'remove') confirmRemoveShare(target);
}

const partyColumns = computed((): DataTableColumns<ContractualPartyUserShareInDb> => [
  {
    title: 'Party',
    key: 'contractual_party',
    render: row =>
      renderTitle(contractualPartyRoute(row.contractual_party.uid), row.contractual_party.name),
  },
  {
    title: 'Access',
    key: 'permissions',
    width: 200,
    render: row =>
      h(GaiaTag, { variant: 'muted', size: 'small' }, () =>
        formatAccessPermissions(row.permissions)
      ),
  },
  {
    title: '',
    key: 'actions',
    width: 64,
    align: 'right',
    render: row =>
      isAdmin.value
        ? renderRowMenu(
            SHARE_MENU,
            key => {
              onShareMenu(
                {
                  kind: 'party',
                  uid: row.contractual_party.uid,
                  name: row.contractual_party.name,
                  permissions: row.permissions,
                },
                key
              );
            },
            `Actions for ${row.contractual_party.name}`
          )
        : h('span'),
  },
]);

function partyRowProps(row: ContractualPartyUserShareInDb): Record<string, unknown> {
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      void router.push(contractualPartyRoute(row.contractual_party.uid));
    },
  };
}

function roleRowProps(row: PermissionRoleInDb): Record<string, unknown> {
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      openRole(row);
    },
  };
}

function contractRoute(row: ContractUserShareInDb): RouteLocationRaw {
  return contractDetailRoute(row.contract.uid);
}

function contractRowProps(row: ContractUserShareInDb): Record<string, unknown> {
  return {
    style: { cursor: 'pointer' },
    onClick: (): void => {
      void router.push(contractRoute(row));
    },
  };
}

// ==================== Lifecycle ====================

async function loadAll(): Promise<void> {
  // The page is org-admin only; the router enforces it, this keeps a stale tab honest.
  if (!isAdmin.value) {
    await router.push({ name: 'settings-profile' });
    return;
  }
  // The member first: their roles and shares hang off them.
  await fetchUser();
  if (!user.value) return;
  await Promise.all([fetchRoles(), fetchSharedContracts(), fetchSharedParties()]);
}

onMounted(() => {
  void loadAll();
});

watch(userUid, uid => {
  if (uid) void loadAll();
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.organisation }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'settings-users' }">{{ navLabels.people }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ user ? displayName : 'Member' }}</n-breadcrumb-item>
    </template>

    <n-spin :show="loading && !user">
      <template v-if="user">
        <!-- Header: who they are, and what you can do to their membership -->
        <n-flex vertical :size="16" :wrap="false" :style="{ marginBottom: '20px' }">
          <n-flex justify="space-between" align="center" :size="16" :wrap="false">
            <n-flex align="center" :size="14" :wrap="false" :style="{ minWidth: 0 }">
              <gaia-avatar :size="48">{{ initials }}</gaia-avatar>
              <n-flex vertical :size="4" :style="{ minWidth: 0 }">
                <n-flex align="center" :size="10" :wrap="false">
                  <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
                    {{ displayName }}
                  </gaia-header>
                  <gaia-tag v-if="holdsAdminRole" variant="muted" size="small">Admin</gaia-tag>
                  <gaia-tag v-if="isSelf" variant="muted" size="small">You</gaia-tag>
                  <gaia-status-chip v-if="!user.is_active" tone="warning" size="small">
                    Deactivated
                  </gaia-status-chip>
                </n-flex>
                <gaia-text size="body" tone="subtle">
                  {{ user.full_name ? `${user.email} · ` : '' }}Member since
                  {{ formatDateForDisplay(user.created_at ?? null) }}
                </gaia-text>
              </n-flex>
            </n-flex>
            <n-dropdown
              v-if="isAdmin"
              trigger="click"
              placement="bottom-end"
              :options="memberActions.menuOptions(user)"
              @select="(key: string | number) => user && memberActions.run(user, String(key))"
            >
              <gaia-button aria-label="Member actions" title="Member actions">
                <template #icon>
                  <n-icon :component="IconMore" />
                </template>
              </gaia-button>
            </n-dropdown>
          </n-flex>
          <gaia-tab-bar
            :items="tabs"
            :active="currentTab"
            @select="(key: string) => (activeTab = key)"
          />
        </n-flex>

        <!-- Roles -->
        <template v-if="currentTab === 'roles'">
          <gaia-empty-state v-if="roles.length === 0 && !loadingRoles" title="No roles yet" dashed>
            <template #icon>
              <n-icon :component="IconShieldCheck" :size="18" />
            </template>
            Roles decide what {{ displayName }} can do in the organisation.
            <template v-if="isAdmin" #actions>
              <gaia-button type="primary" @click="showAssignRole = true">Assign a role</gaia-button>
            </template>
          </gaia-empty-state>
          <template v-else>
            <n-flex
              justify="space-between"
              align="center"
              :size="12"
              :style="{ marginBottom: '12px' }"
            >
              <gaia-text size="control" tone="subtle">
                {{ displayName }} can do everything these roles allow.
              </gaia-text>
              <gaia-button v-if="isAdmin" type="primary" @click="showAssignRole = true">
                <template #icon>
                  <n-icon :component="IconShieldCheck" />
                </template>
                Assign role
              </gaia-button>
            </n-flex>
            <gaia-table
              :columns="roleColumns"
              :data="roles"
              :loading="loadingRoles"
              :row-key="(row: PermissionRoleInDb) => row.uid"
              :row-props="roleRowProps"
            />
          </template>
        </template>

        <!-- Contracts shared directly with them -->
        <template v-else-if="currentTab === 'contracts'">
          <gaia-empty-state
            v-if="sharedContracts.length === 0 && !loadingContracts"
            title="No contracts shared directly"
            dashed
          >
            <template #icon>
              <n-icon :component="IconDocumentText" :size="18" />
            </template>
            Contracts shared with {{ displayName }} personally show up here. Access through their
            roles is on each role’s page.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              Shared with {{ displayName }} personally, on top of what their roles give them.
            </gaia-text>
            <gaia-table
              :columns="contractColumns"
              :data="sharedContracts"
              :loading="loadingContracts"
              :row-key="(row: ContractUserShareInDb) => row.contract.uid"
              :row-props="contractRowProps"
            />
          </template>
        </template>

        <!-- Contractual parties shared directly with them -->
        <template v-else-if="currentTab === 'parties'">
          <gaia-empty-state
            v-if="sharedParties.length === 0 && !loadingParties"
            title="No parties shared directly"
            dashed
          >
            <template #icon>
              <n-icon :component="IconPeople" :size="18" />
            </template>
            Contractual parties shared with {{ displayName }} personally show up here. Access
            through their roles is on each role’s page.
          </gaia-empty-state>
          <template v-else>
            <gaia-text size="control" tone="subtle" tag="p" :style="{ margin: '0 0 12px' }">
              Shared with {{ displayName }} personally, on top of what their roles give them.
            </gaia-text>
            <gaia-table
              :columns="partyColumns"
              :data="sharedParties"
              :loading="loadingParties"
              :row-key="(row: ContractualPartyUserShareInDb) => row.contractual_party.uid"
              :row-props="partyRowProps"
            />
          </template>
        </template>

        <!-- Vault folders and files shared directly with them. Kept mounted
             (v-show) so the tab shows its count before it is opened. -->
        <div v-show="currentTab === 'files'">
          <vault-shares-table
            :subject="{ kind: 'user', uid: userUid }"
            :subject-name="displayName"
            :manageable="isAdmin"
            @count="(count: number) => (sharedVaultItemCount = count)"
          />
        </div>
      </template>

      <gaia-empty-state v-else-if="!loading" title="Member not found">
        They may have left the organisation, or the link is out of date.
        <template #actions>
          <gaia-button @click="router.push({ name: 'settings-users' })">Back to people</gaia-button>
        </template>
      </gaia-empty-state>
    </n-spin>

    <assign-role-to-user-modal
      v-model="showAssignRole"
      :assigned-role-uids="roles.map(role => role.uid)"
      :assigning-role-uid="assigningRoleUid"
      @assign="handleAssignRole"
    />

    <organisation-severance-modal
      v-if="severance"
      :show="!!severance"
      :mode="severance.mode"
      :organisation-name="organizationStore.activeOrganization?.name ?? ''"
      :user-email="severance.email"
      :loading="severanceLoading"
      :error="severanceError"
      @update:show="(open: boolean) => !open && closeSeverance()"
      @confirm="confirmSeverance"
    />

    <!-- Change a share's access -->
    <gaia-modal
      :show="editingShare !== null"
      width="400px"
      title="Change access"
      :mask-closable="!savingShare"
      :close-on-esc="!savingShare"
      @update:show="(value: boolean) => !value && (editingShare = null)"
    >
      <n-flex vertical :size="14" :wrap="false">
        <gaia-text size="body" tone="subtle" :style="{ lineHeight: 1.55 }">
          What {{ displayName }} can do with {{ editingShare?.name }}.
        </gaia-text>
        <gaia-select
          v-model:value="editShareBitmask"
          :options="ACCESS_LEVEL_OPTIONS"
          placeholder="Choose access"
          style="width: 100%"
        />
      </n-flex>
      <template #action>
        <n-flex justify="end" :size="8" :wrap="false">
          <gaia-button :disabled="savingShare" @click="editingShare = null">Cancel</gaia-button>
          <gaia-button
            type="primary"
            :loading="savingShare"
            :disabled="editShareBitmask === 0 || editShareBitmask === editingShare?.permissions"
            @click="saveShare"
          >
            Save access
          </gaia-button>
        </n-flex>
      </template>
    </gaia-modal>
  </Layout>
</template>
